// src/features/medicine-inventory/pages/Dashboard.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { FaPills, FaTags, FaExchangeAlt, FaExclamationTriangle } from 'react-icons/fa';
import { useInventory } from '../context/InventoryContext';
import { formatCurrency } from '../utils/helpers';

const Dashboard = () => {
  const { medicines, stockMovements, stats } = useInventory();
  const recentMovements = stockMovements.slice(0, 5);

  const lowStockMedicines = medicines
    .filter(m => m.currentStock <= m.reorderLevel)
    .slice(0, 5);

  const calculateStats = () => {
    const totalMedicines = medicines.length;
    const totalCategories = new Set(medicines.map(m => m.categoryId)).size;
    const lowStockItems = medicines.filter(m => m.currentStock <= m.reorderLevel).length;
    const totalValue = medicines.reduce((sum, m) => sum + (m.currentStock * m.price), 0);
    
    return { totalMedicines, totalCategories, lowStockItems, totalValue };
  };

  const currentStats = stats || calculateStats();

  const StatCard = ({ title, value, icon: Icon, color, link }) => (
    <Link to={link} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </Link>
  );

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Medicines"
          value={currentStats.totalMedicines}
          icon={FaPills}
          color="bg-blue-600"
          link="/inventory/medicines"
        />
        <StatCard
          title="Categories"
          value={currentStats.totalCategories}
          icon={FaTags}
          color="bg-green-600"
          link="/inventory/categories"
        />
        <StatCard
          title="Low Stock Items"
          value={currentStats.lowStockItems}
          icon={FaExclamationTriangle}
          color="bg-yellow-600"
          link="/inventory/medicines"
        />
        <StatCard
          title="Total Value"
          value={formatCurrency(currentStats.totalValue)}
          icon={FaExchangeAlt}
          color="bg-purple-600"
          link="/inventory/stock-movements"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Low Stock Alert */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Low Stock Alert</h2>
          </div>
          <div className="p-6">
            {lowStockMedicines.length > 0 ? (
              <div className="space-y-4">
                {lowStockMedicines.map(medicine => (
                  <div key={medicine.id} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{medicine.name}</p>
                      <p className="text-sm text-gray-500">{medicine.manufacturer}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">{medicine.currentStock} {medicine.unit}s</p>
                      <p className="text-sm text-red-600">Reorder at: {medicine.reorderLevel}</p>
                    </div>
                  </div>
                ))}
                <Link
                  to="/inventory/medicines"
                  className="block text-center text-sm text-blue-600 hover:text-blue-800 mt-4"
                >
                  View All Medicines
                </Link>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No low stock items</p>
            )}
          </div>
        </div>

        {/* Recent Stock Movements */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Recent Stock Movements</h2>
          </div>
          <div className="p-6">
            {recentMovements.length > 0 ? (
              <div className="space-y-4">
                {recentMovements.map(movement => (
                  <div key={movement.id} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{movement.medicineName}</p>
                      <p className="text-sm text-gray-500">
                        {movement.type === 'PURCHASE' ? 'Purchase' : 'Issue'} • 
                        {new Date(movement.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`font-medium ${
                        movement.type === 'PURCHASE' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {movement.type === 'PURCHASE' ? '+' : '-'}{movement.quantity}
                      </p>
                      <p className="text-sm text-gray-500">{formatCurrency(movement.totalPrice)}</p>
                    </div>
                  </div>
                ))}
                <Link
                  to="/inventory/stock-movements"
                  className="block text-center text-sm text-blue-600 hover:text-blue-800 mt-4"
                >
                  View All Movements
                </Link>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent movements</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;